import type { AssetView } from "./types";

/**
 * Renders an asset reference as a single human-friendly string.
 *
 * Rules:
 *   - Resolved view with assetTag → "ASS-024 · Laptop · Dell Latitude 7440 (7832e919-…)"
 *   - Resolved view without assetTag → "Laptop · Dell Latitude 7440 · SN ABC123 (7832e919-…)"
 *   - Unresolved (pending lookup, 404) → just the id
 *
 * The asset tag, when present, is the first thing operators recognize —
 * it's the sticker on the device. Falling back to type/make/model/SN
 * keeps the label informative for assets imported before tagging.
 *
 * The id is always shown alongside the friendly label so admins still
 * have the canonical reference to copy/paste into other tools when
 * troubleshooting an assignment.
 */
export function formatAssetDisplay(
    asset: AssetView | null | undefined,
    fallbackId: string,
): string {
    if (asset === null || asset === undefined) {
        return fallbackId;
    }

    const friendly = pickFriendly(asset);
    return `${friendly} (${asset.id})`;
}

function pickFriendly(asset: AssetView): string {
    const sep = " \u00b7 ";

    if (
        asset.assetTag !== null &&
        asset.assetTag !== undefined &&
        asset.assetTag !== ""
    ) {
        const makeModel = joinTruthy([asset.make, asset.model], " ");
        return joinTruthy(
            [asset.assetTag, asset.type, makeModel === "" ? null : makeModel],
            sep,
        );
    }

    const makeModel = joinTruthy([asset.make, asset.model], " ");
    return joinTruthy(
        [
            asset.type,
            makeModel === "" ? null : makeModel,
            asset.serialNumber !== "" ? `SN ${asset.serialNumber}` : null,
        ],
        sep,
    );
}

function joinTruthy(parts: readonly (string | null | undefined)[], sep: string): string {
    return parts
        .filter((p): p is string => p !== null && p !== undefined && p !== "")
        .join(sep);
}
